import { useState, useRef, useEffect, useContext } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, Building2, Plane, FileText, ArrowRight } from 'lucide-react';
import DashboardContext from '../../../../../../context/dashboard/DashboardContext';
import { toast } from 'react-hot-toast';
import GenericUploadZone from '../AiVoucher/GenericUploadZone';

const MAX_TOTAL_FILES = 30;
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB

const EnrichVoucherModal = ({ isOpen, onClose, existingData }) => {
    const [hotelFiles, setHotelFiles] = useState([]);
    const [travelFiles, setTravelFiles] = useState([]);
    const [itineraryFiles, setItineraryFiles] = useState([]);

    const [isHotelDragging, setIsHotelDragging] = useState(false);
    const [isTravelDragging, setIsTravelDragging] = useState(false);
    const [isItineraryDragging, setIsItineraryDragging] = useState(false);

    const travelZoneRef = useRef(null);
    const itineraryZoneRef = useRef(null);

    const {
        analysisState,
        startVoucherAnalysis,
        resetAnalysis
    } = useContext(DashboardContext);

    // Cleanup on open/close
    useEffect(() => {
        if (isOpen) {
            resetAnalysis();
            setHotelFiles([]);
            setTravelFiles([]);
            setItineraryFiles([]);
        }
    }, [isOpen, resetAnalysis]);

    // Close modal if minimized
    useEffect(() => {
        if (analysisState.isMinimized && isOpen) {
            onClose();
        }
    }, [analysisState.isMinimized, isOpen, onClose]);

    // File validation helper matching outer creator
    const validateFiles = (incomingFiles, currentZoneFiles, otherZonesFiles = []) => {
        const totalOtherCount = otherZonesFiles.reduce((acc, curr) => acc + curr.length, 0);
        const totalCount = currentZoneFiles.length + totalOtherCount;

        if (totalCount >= MAX_TOTAL_FILES) {
            toast.error(`Maximum limit of ${MAX_TOTAL_FILES} files reached.`);
            return [];
        }

        const validTypes = incomingFiles.filter(file =>
            file.type === 'application/pdf' ||
            file.type.startsWith('image/') ||
            file.type.startsWith('video/')
        );

        if (validTypes.length < incomingFiles.length) {
            toast.error("Some files were skipped. Only PDFs, Images, and Videos are allowed.");
        }

        const sizeChecked = validTypes.filter(file => {
            if (file.size > MAX_FILE_SIZE) {
                toast.error(`${file.name} exceeds 50MB limit.`);
                return false;
            }
            return true;
        });

        const remainingSlots = MAX_TOTAL_FILES - totalCount;
        if (sizeChecked.length > remainingSlots) {
            toast.error(`Only ${remainingSlots} more files can be added.`);
            return sizeChecked.slice(0, remainingSlots);
        }

        return sizeChecked;
    };

    const createDropHandler = (setter, currentFiles, otherZones) => (e) => {
        e.preventDefault();
        const droppedFiles = Array.from(e.dataTransfer.files);
        const validFiles = validateFiles(droppedFiles, currentFiles, otherZones);

        if (validFiles.length > 0) {
            setter(prev => {
                const existingNames = new Set(prev.map(f => f.name + f.size));
                const newFiles = validFiles.filter(f => !existingNames.has(f.name + f.size));
                if (newFiles.length < validFiles.length) {
                    toast.error("Duplicate files were ignored.");
                }
                return [...prev, ...newFiles];
            });
        }
    };

    const handleStartExtraction = async () => {
        if (hotelFiles.length === 0 && travelFiles.length === 0 && itineraryFiles.length === 0) {
            toast.error("Please upload at least one document to enrich.");
            return;
        }
        try {
            const allDocFiles = [...hotelFiles, ...travelFiles];
            onClose();
            await startVoucherAnalysis(allDocFiles, itineraryFiles, existingData);
        } catch (err) {
            console.error("Enrichment error:", err);
        }
    };

    if (!isOpen) return null;

    return createPortal(
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-2.5 sm:p-4 md:p-6 overflow-hidden">
            <div
                className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-300"
                onClick={onClose}
            ></div>

            <div className="relative w-full max-w-6xl bg-white rounded-[24px] sm:rounded-[32px] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300 max-h-[94dvh] sm:max-h-[92vh] flex flex-col border border-slate-100">

                {/* Header */}
                <div className="px-4 sm:px-8 py-3.5 sm:py-5 border-b border-slate-100 flex items-center justify-between bg-white shrink-0">
                    <div className="flex items-center gap-2.5 sm:gap-3.5 min-w-0">
                        <div className="w-9 h-9 sm:w-11 sm:h-11 bg-teal-50 text-[#067978] ring-1 ring-teal-100 rounded-xl sm:rounded-2xl flex items-center justify-center shadow-xs shrink-0">
                            <Sparkles size={18} className="text-[#067978] sm:hidden" />
                            <Sparkles size={22} className="text-[#067978] hidden sm:block" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-base sm:text-xl font-bold text-slate-800 tracking-tight leading-tight truncate">Smart Document Enrichment</h2>
                            <p className="text-[9px] sm:text-[11px] text-slate-400 font-bold uppercase tracking-wider mt-0.5 truncate">Two-Way Intelligent Extraction</p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 sm:p-2.5 hover:bg-slate-100 rounded-full text-slate-400 hover:text-slate-600 transition-colors cursor-pointer shrink-0 ml-2"
                        aria-label="Close modal"
                    >
                        <X size={18} className="sm:hidden" />
                        <X size={20} className="hidden sm:block" />
                    </button>
                </div>

                {/* Body Content - Scrollable Cards */}
                <div className="p-3.5 sm:p-6 md:p-8 flex-1 overflow-y-auto min-h-0">
                    {/* 3 Upload Zones in Grid Matching Outer Creator */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 items-stretch mb-2">
                        <GenericUploadZone
                            id="enrich-hotel-upload"
                            stepBadge="STEP 1 • OPTIONAL"
                            title="Hotel Bookings"
                            subtitle="Upload {highlight}Hotel{highlight} Confirmations"
                            description="Drop hotel booking PDFs or screenshots. Auto-extracts hotel, room & guest details."
                            icon={Building2}
                            colorClass="teal"
                            files={hotelFiles}
                            setFiles={setHotelFiles}
                            handleDragOver={(e) => { e.preventDefault(); setIsHotelDragging(true); }}
                            handleDragLeave={(e) => { e.preventDefault(); setIsHotelDragging(false); }}
                            handleDrop={(e) => { setIsHotelDragging(false); createDropHandler(setHotelFiles, hotelFiles, [travelFiles, itineraryFiles])(e); }}
                            isDragging={isHotelDragging}
                            isSubmitting={analysisState.isAnalyzing}
                        />

                        <GenericUploadZone
                            id="enrich-travel-upload"
                            containerRef={travelZoneRef}
                            stepBadge="STEP 2 • OPTIONAL"
                            title="Travel Tickets"
                            subtitle="Upload {highlight}Travel{highlight} Tickets"
                            description="Flight, train, or bus vouchers. Auto-extracts dates and timings into schedule."
                            icon={Plane}
                            colorClass="teal"
                            files={travelFiles}
                            setFiles={setTravelFiles}
                            handleDragOver={(e) => { e.preventDefault(); setIsTravelDragging(true); }}
                            handleDragLeave={(e) => { e.preventDefault(); setIsTravelDragging(false); }}
                            handleDrop={(e) => { setIsTravelDragging(false); createDropHandler(setTravelFiles, travelFiles, [hotelFiles, itineraryFiles])(e); }}
                            isDragging={isTravelDragging}
                            isSubmitting={analysisState.isAnalyzing}
                        />

                        <GenericUploadZone
                            id="enrich-itinerary-upload"
                            containerRef={itineraryZoneRef}
                            stepBadge="STEP 3 • OPTIONAL"
                            title="Detailed Itinerary"
                            subtitle="Upload {highlight}Detailed{highlight} Plan"
                            description="Day-by-day itinerary or package PDF to enrich schedule & activities."
                            icon={FileText}
                            colorClass="teal"
                            files={itineraryFiles}
                            setFiles={setItineraryFiles}
                            handleDragOver={(e) => { e.preventDefault(); setIsItineraryDragging(true); }}
                            handleDragLeave={(e) => { e.preventDefault(); setIsItineraryDragging(false); }}
                            handleDrop={(e) => { setIsItineraryDragging(false); createDropHandler(setItineraryFiles, itineraryFiles, [hotelFiles, travelFiles])(e); }}
                            isDragging={isItineraryDragging}
                            isSubmitting={analysisState.isAnalyzing}
                        />
                    </div>
                </div>

                {/* Dedicated Footer Bar - Generous padding & separation */}
                <div className="px-4 sm:px-8 py-3.5 sm:py-5 border-t border-slate-100 bg-white flex flex-col items-center shrink-0">
                    <button
                        onClick={handleStartExtraction}
                        disabled={analysisState.isAnalyzing || (hotelFiles.length === 0 && travelFiles.length === 0 && itineraryFiles.length === 0)}
                        className="w-full max-w-md py-3 sm:py-4 px-6 sm:px-8 rounded-full bg-[#067978] hover:bg-[#056362] text-white font-bold text-sm sm:text-base shadow-lg shadow-teal-700/20 hover:shadow-xl hover:shadow-teal-700/30 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:transform-none disabled:shadow-none cursor-pointer flex items-center justify-center gap-2.5 sm:gap-3 group"
                    >
                        {analysisState.isAnalyzing ? (
                            <span className="flex items-center justify-center gap-2 sm:gap-2.5">
                                <span className="animate-spin w-4 h-4 sm:w-5 sm:h-5 border-2 border-white/30 border-t-white rounded-full" />
                                <span>Processing documents...</span>
                            </span>
                        ) : (
                            <span className="flex items-center justify-center gap-2 sm:gap-2.5">
                                <span>Process & Enrich Voucher</span>
                                <ArrowRight size={18} className="sm:w-5 sm:h-5 transition-transform group-hover:translate-x-1" />
                            </span>
                        )}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default EnrichVoucherModal;
